import React, { useState } from 'react';
import { Modal, Tabs, Segmented, message } from 'antd';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useCurrentUser } from '../../utils/CurrentUserContext';
import { Placement } from '../../__generated__/graphql';

interface GetTagsModalProps {
  open: boolean;
  onCancel: () => void;
  placement?: Placement;
}

const CodeBlock: React.FC<{ code: string; label: string }> = ({ code, label }) => {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    message.success(`${label} copied to clipboard`);
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 pr-12 text-sm overflow-x-auto whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
        title={`Copy ${label}`}
      >
        <DocumentDuplicateIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

const DirectTab: React.FC<{ publisherSlug: string; placementKey: string }> = ({ publisherSlug, placementKey }) => (
  <div className="space-y-6">
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-1">Master script</h4>
      <p className="text-sm text-gray-500 mb-2">
        Must be implemented directly on page or through any Tag Manager
      </p>
      <CodeBlock
        label="Master script"
        code={`<script async src="https://scripts.contimo.app/pub/${publisherSlug}/drivers.js"></script>`}
      />
    </div>

    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-1">Placement</h4>
      <p className="text-sm text-gray-500 mb-2">
        Implement this HTML tag in places where you want Contimo widget to appear. If you want multiple widgets per page, it is recommended to have multiple distinct placements in Contimo
      </p>
      <CodeBlock
        label="Placement tag"
        code={`<contimo-widget placement-key="${placementKey}"></contimo-widget>`}
      />
    </div>
  </div>
);

type AdServerType = 'Google Ad Manager' | 'Other';

const AdServerTab: React.FC<{ publisherSlug: string; placementKey: string }> = ({ publisherSlug, placementKey }) => {
  const [adServer, setAdServer] = useState<AdServerType>('Google Ad Manager');

  const gamCode = `<script async \n  src="https://scripts.contimo.app/pub/${publisherSlug}/drivers.js" \n  data-tc-string="\${GDPR_CONSENT_1299}"></script>\n\n<contimo-widget placement-key="${placementKey}" dummy="%%PREVIEW_MODE%%"></contimo-widget>`;

  const otherCode = `<script async \n  src="https://scripts.contimo.app/pub/${publisherSlug}/drivers.js" \n  data-tc-string="\${GDPR_CONSENT_1299}"></script>\n\n<contimo-widget placement-key="${placementKey}"></contimo-widget>`;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Implement the following code as HTML-creative in your Ad-server
      </p>
      <Segmented
        value={adServer}
        onChange={(value) => setAdServer(value as AdServerType)}
        options={['Google Ad Manager', 'Other']}
      />
      <CodeBlock
        label="Ad-server tag"
        code={adServer === 'Google Ad Manager' ? gamCode : otherCode}
      />
    </div>
  );
};

export default function GetTagsModal({ open, onCancel, placement }: GetTagsModalProps) {
  const { user } = useCurrentUser();
  const [activeKey, setActiveKey] = useState('direct');

  return (
    <Modal
      open={open}
      title="Get tags"
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        items={[
          {
            key: 'direct',
            label: 'Direct',
            children: (
              <DirectTab
                publisherSlug={user.company.slug}
                placementKey={placement?.key || ''}
              />
            ),
          },
          {
            key: 'ad-server',
            label: 'Ad-server',
            children: (
              <AdServerTab
                publisherSlug={user.company.slug}
                placementKey={placement?.key || ''}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}
